/**
 * 작성자 : 박승호
 * 날짜 : 2024.09.27
 * 기능 : PC에서 왼쪽 화면과 오른쪽 화면에서 상호작용이 일어나야 하는 부분에서 모바일에서 모달창으로 대체하기 위한 용도
 *
 */

import { useTranslation } from 'react-i18next';
import { BtnBlue, BtnGray } from '../Button';
import styled from 'styled-components';
import { useEffect } from 'react';

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5010;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 60%);
  backdrop-filter: blur(0.5rem);
  user-select: none;
  > .modal {
    width: 40rem;
    width: 64rem;
    max-width: calc(100vw - 2rem);
    max-width: calc(100dvw - 2rem);
    border-radius: 0.5rem;
    overflow: hidden;
    max-height: calc(100vh - 2rem);
    max-height: calc(100dvh - 2rem);
    background-color: var(--board);
    color: var(--text-primary);
    > .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.5rem;
      border-bottom: 1px solid var(--outline);
      color: var(--text-primary);
      > .title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.5rem;
      }
      > .closeButton {
        border-radius: 0.5rem;
        margin: 0.375rem;
        padding: 0.25rem;
        display: flex;
        cursor: pointer;
        > .material-symbols-rounded {
          font-variation-settings: 'FILL' 0, 'wght' 600, 'GRAD' 0, 'opsz' 24;
          font-size: 1.375rem;
          color: var(--text-primary);
        }
        &:hover {
          background-color: var(--primary);
          box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.1);
          .material-symbols-rounded {
            color: var(--tonal-deep-blue);
          }
        }
      }
    }
    .body {
      max-height: calc(100vh - 9.75rem);
      max-height: calc(100dvh - 9.75rem);
      overflow: hidden;
      position: relative;
      > .content-container {
        max-height: inherit;
        overflow: auto;
        -webkit-overflow-scrolling: touch; /* iOS 기기에서 스크롤 성능 개선 */
        flex: 1 0 60%;
        display: flex;
        flex-direction: column;
        border-left: 1px solid var(--outline);
        background-color: var(--board);
        > .informations {
          padding: 0 0.75rem;
          padding-top: 0.75rem;
          display: flex;
          flex-direction: column;
        }
        > .textarea-container {
          overflow: auto;
          width: 100%;
          padding: 0 0.75rem;
          padding-bottom: 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          > div {
            position: sticky;
            top: 0;
          }
        }
      }
    }
    .footer {
      display: flex;
      justify-content: space-between;
      background-color: var(--board);
      border-top: 1px solid var(--outline);
      padding: 0.5rem;
      > .toggles,
      > .buttons {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        > .label {
          font-size: 0.875rem;
          letter-spacing: normal;
          color: var(--text-tertiary);
          padding: 0 0.5rem;
        }
      }
    }
  }
  > .cctvWrapperHidden {
    visibility: hidden;
    height: 0.1rem;
    width: 0.1rem;
    position: absolute;
    left: 0;
    top: 0;
  }
`;

interface Props {
  openModal?: any;
  setOpenModal?: any;
  title: string;
  btnConfirmName?: string;
  handleClose?: any;
  handleSave?: any;
  children: React.ReactNode;
}

const CustomMobileModal = ({ openModal, setOpenModal, title, btnConfirmName = '', handleClose, handleSave, children }: Props) => {
  const { t } = useTranslation();

  const onClickClose = () => {
    setOpenModal((prev: any) => ({ ...prev, status: false }));
  };

  return (
    <Root onClick={handleClose} role='presentation'>
      <div
        className='modal'
        role='presentation'
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className='header'>
          <div className='modalHeaderTitle'>{title}</div>
          <div className='closeButton' onClick={onClickClose} role='presentation'>
            <span className='material-symbols-rounded'>close</span>
          </div>
        </div>

        {btnConfirmName !== '' ? (
          <div>
            <div className='body'>{children}</div>

            <div className='footer'>
              <BtnGray onClick={onClickClose}>{t('닫기')}</BtnGray>
              <BtnBlue onClick={handleSave}>{btnConfirmName}</BtnBlue>
            </div>
          </div>
        ) : (
          <div>{children}</div>
        )}
      </div>
    </Root>
  );
};

export default CustomMobileModal;
